import type {CustomerData} from "./CustomerData.ts";
import {HTTP} from "./HTTP.ts";
import type {PluginData} from "./PluginData.ts";
import type {PublishConfig} from "./PublishConfig.ts";
import type {PublishState} from "./PublishState.ts";

/**
 * Represents a server connection that provides methods to interact with customer
 * and plugin data through HTTP requests.
 */
class Server {
    private readonly address: string;

    /**
     * Constructs a new Server instance with the specified adress and port.
     * @param {string} address - The adress of the server.
     */
    constructor(address: string) {
        this.address = address;
    }

    /**
     * Retrieves a list of customers from the server.
     * @param {string} filter - An optional filter string to apply to the customer list.
     * @return {Promise<CustomerData[]>} A promise that resolves with an array of customer data.
     */
    public async getCustomerList(filter?: string): Promise<CustomerData[]> {
        return this.fetchData(HTTP.GET, `customer/${filter}`);
    }

    /**
     * Retrieves a list of plugins from the server.
     * @param {string} filter - The filter string to apply to the plugin list.
     * @return {Promise<PluginData[]>} A promise that resolves with an array of plugin data.
     */
    public async getPluginList(filter: string): Promise<PluginData[]> {
        return this.fetchData(HTTP.GET, `customer/plugins/${filter}`);
    }

    /**
     * Publishes a plugin to the server.
     * @param {PublishConfig} config - The configuration object containing the plugin details.
     * @return {Promise<void>} A promise that resolves when the plugin is successfully published.
     */
    public async publishPlugin(config: PublishConfig): Promise<PublishState> {
        return this.fetchData(HTTP.POST, `plugin/publish`, config);
    }

    /**
     * Sends a POST request to the specified target with the provided data and returns the parsed response.
     *
     *
     * @param {string} method - The HTTP method to use for the request. Must be either 'GET' or 'POST'.
     * @param {string} target - The target endpoint to send the request to.
     * @param {U} data - The payload to send in the body of the request.
     * @return {Promise<T>} A promise that resolves with the parsed response data of type T.
     * @template T - The expected type of the returned response data.
     * @template U - The type of the payload being sent in the request.
     */
    private async fetchData<T, U>(method: string, target: string, data?: U): Promise<T> {
        console.log('fetching', method, target, data);
        const url = `${this.address}/${target}`;
        const response = method !== HTTP.GET
            ? await fetch(
                url,
                {
                    method: method,
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                }
            )
            : await fetch(
                url,
                {
                    method: method,
                    headers: {
                        'Content-Type': 'application/json'
                    },
                }
            );
        if (!response.ok || response.status !== 200) {
            throw new Error(`Response status: ${response.status}`);
        }
        return await response.json() as Promise<T>;
    }
}

// Development: VITE_API_URL aus .env.development (z.B. http://console-v2.localhost)
// Production: VITE_API_URL ist leer -> verwende aktuelle Origin
const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
const server = new Server(apiUrl);

export default server;
